import { useCallback, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconDownload, IconSettings, IconRefresh } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../store/auth.store";
import {
  usePendingWork,
  usePendingWorkScroll,
  usePendingWorkSummary,
  usePendingWorkCount,
} from "../../hooks/usePendingWork";
import { usePendingWorkView } from "../../hooks/usePendingWorkView";
import {
  pendingWorkError,
  pendingWorkService,
} from "../../services/pendingWork.service";
import { QueuePreferencesModal } from "../../components/pathologist-review/QueuePreferencesModal";
import { PrefixCheckboxFilter } from "../../components/pathologist-review/PrefixCheckboxFilter";
import { PendingWorkColumnHeader } from "../../components/pending-work/PendingWorkColumnHeader";
import { PendingWorkTablePage } from "../../components/pending-work/PendingWorkTablePage";
import { advanceReviewSorts } from "./pathologistReviewQueueView";
import { PENDING_WORK_COLUMNS, PENDING_WORK_KINDS } from "./pendingWorkView";
import type { QueuePreferences } from "./queueColumnConfig";

export default function PendingWorkQueuePage() {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.profile?.id);
  const summary = usePendingWorkSummary();
  const preference = usePendingWorkView(summary.data?.prefixOptions);
  const hasFilters = useMemo(
    () =>
      Boolean(
        preference.view.filters.search ||
          preference.view.filters.cause ||
          preference.view.filters.prefixKeys.length
      ),
    [preference.view.filters]
  );
  const filteredCount = usePendingWorkCount(
    preference.view.filters,
    hasFilters && preference.hydrated && preference.valid && summary.isSuccess
  );
  const count = useMemo(
    () => (hasFilters ? filteredCount : summary),
    [hasFilters, filteredCount, summary]
  );
  const countLabel = useMemo(
    () =>
      preference.hydrated && preference.valid && count.data
        ? `${count.data.total.toLocaleString()} ${
            count.data.total === 1 ? "case" : "cases"
          }`
        : "Counting cases…",
    [count.data, preference.hydrated, preference.valid]
  );
  const query = usePendingWork(
    preference.view,
    preference.hydrated && preference.valid && summary.isSuccess
  );
  const [settings, setSettings] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const rows = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) || [],
    [query.data]
  );
  const columns = useMemo(
    () =>
      preference.view.columns.map(
        (id) => PENDING_WORK_COLUMNS.find((column) => column.id === id)!
      ),
    [preference.view.columns]
  );
  const columnPreferences = useMemo(
    () => ({
      columns: preference.view.columns,
      overdueThresholdDays: preference.view.overdueThresholdDays,
    }),
    [preference.view]
  );
  const causeOptions = useMemo(
    () =>
      PENDING_WORK_KINDS.map((value) => ({
        value,
        label:
          value === "failed_qc"
            ? "Failed QC"
            : value === "qa"
            ? "QA review"
            : value.charAt(0).toUpperCase() + value.slice(1),
      })),
    []
  );
  const handlePrefixes = useCallback(
    (prefixKeys: string[]) =>
      preference.update({
        ...preference.view,
        filters: { ...preference.view.filters, prefixKeys },
      }),
    [preference]
  );
  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      preference.update({
        ...preference.view,
        filters: {
          ...preference.view.filters,
          search: event.currentTarget.value,
        },
      }),
    [preference]
  );
  const handleCause = useCallback(
    (cause: string | null) =>
      preference.update({
        ...preference.view,
        filters: { ...preference.view.filters, cause: cause || "" },
      }),
    [preference]
  );
  const handleSort = useCallback(
    (column: string) =>
      preference.update({
        ...preference.view,
        sorts: advanceReviewSorts(preference.view.sorts, column),
      }),
    [preference]
  );
  const handleOpenSettings = useCallback(() => setSettings(true), []);
  const handleCloseSettings = useCallback(() => setSettings(false), []);
  const handlePreferences = useCallback(
    (value: QueuePreferences) => {
      if (!value.columns.length) {
        notifications.show({
          color: "red",
          message: "Choose at least one queue column.",
          autoClose: 4000,
        });
        return;
      }
      preference.update({ ...preference.view, ...value });
      setSettings(false);
    },
    [preference]
  );
  const handleMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage)
      void query.fetchNextPage();
  }, [query]);
  const scrollRef = usePendingWorkScroll(
    handleMore,
    Boolean(query.hasNextPage) && !query.isFetchingNextPage
  );
  const handleRefresh = useCallback(() => {
    void summary.refetch();
    void query.refetch();
    void queryClient.invalidateQueries({
      queryKey: ["pending-work-details", userId],
    });
    if (hasFilters) void filteredCount.refetch();
  }, [query, summary, hasFilters, filteredCount, queryClient, userId]);
  const handleSaveDefault = useCallback(async () => {
    setSavingDefault(true);
    try {
      await preference.saveDefault();
      notifications.show({
        message: "Pending List default saved.",
        color: "green",
        autoClose: 4000,
      });
    } catch {
      notifications.show({
        message: "Your default could not be saved. Try again.",
        color: "red",
        autoClose: 4000,
      });
    } finally {
      setSavingDefault(false);
    }
  }, [preference]);
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await pendingWorkService.exportExcel(
        preference.view.filters,
        preference.view.sorts
      );
      if (useAuthStore.getState().profile?.id !== userId) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "pending-work.xlsx";
      link.click();
      URL.revokeObjectURL(url);
      notifications.show({
        message: "The complete filtered Pending List export is ready.",
        color: "green",
        autoClose: 4000,
      });
    } catch (error) {
      notifications.show({
        message: pendingWorkError(error),
        color: "red",
        autoClose: 4000,
      });
    } finally {
      setExporting(false);
    }
  }, [preference.view.filters, preference.view.sorts, userId]);
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>Pending List</Title>
          <Text size="sm" c="dimmed">
            Cases with pending work.
          </Text>
        </div>
        <Group>
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handleExport}
            loading={exporting}
            disabled={!preference.valid || !summary.isSuccess}
          >
            Export Excel
          </Button>
          <ActionIcon
            variant="default"
            size="lg"
            aria-label="Refresh Pending List"
            onClick={handleRefresh}
          >
            <IconRefresh size={18} />
          </ActionIcon>
          <ActionIcon
            variant="default"
            size="lg"
            aria-label="Queue preferences"
            onClick={handleOpenSettings}
            disabled={!preference.hydrated}
          >
            <IconSettings size={18} />
          </ActionIcon>
        </Group>
      </Group>
      <Paper withBorder p="md">
        <Stack gap="sm">
          <Group align="end">
            <TextInput
              label="Search cases or patients"
              value={preference.view.filters.search}
              onChange={handleSearch}
              maxLength={120}
              disabled={!preference.valid}
            />
            <Select
              label="Pending cause"
              data={causeOptions}
              value={preference.view.filters.cause || null}
              onChange={handleCause}
              clearable
              placeholder="All pending causes"
              disabled={!preference.valid}
            />
            <Button
              variant="light"
              onClick={handleSaveDefault}
              loading={savingDefault}
              disabled={!preference.valid || !preference.defaultHydrated}
            >
              Save Default
            </Button>
            <Button
              variant="light"
              onClick={preference.applyDefault}
              disabled={!preference.savedDefault || !preference.valid}
            >
              Apply Default
            </Button>
            <Button
              variant="subtle"
              onClick={preference.reset}
              disabled={!preference.hydrated}
            >
              Reset View
            </Button>
            <Text size="sm" fw={600} ml="auto" role="status" aria-live="polite">
              {count.isError ? "Case count unavailable" : countLabel}
            </Text>
          </Group>
          {count.isError && (
            <Alert color="red" title="Case count unavailable" role="alert">
              The matching case count could not be loaded. Refresh to try again.
            </Alert>
          )}
          {summary.data && (
            <PrefixCheckboxFilter
              options={summary.data.prefixOptions}
              value={preference.view.filters.prefixKeys}
              onChange={handlePrefixes}
              disabled={!preference.valid}
            />
          )}
          <Group gap="xs">
            <Badge color="red">Failed QC</Badge>
            <Badge color="red">STAT / rush</Badge>
            <Badge color="yellow">Pending slides</Badge>
            <Badge color="blue">Pending send-outs</Badge>
            <Badge color="green">Other unfinished work</Badge>
            <Text size="xs">
              Oldest TAT first by default. Numbered arrows show sort priority.
            </Text>
          </Group>
        </Stack>
      </Paper>
      {(!preference.hydrated || summary.isLoading) && (
        <Loader aria-label="Loading Pending List" />
      )}
      {preference.hydrated && !preference.valid && (
        <Alert color="red" title="Saved view cannot be applied" role="alert">
          Your saved filters or columns are invalid or no longer available.
          Reset View to choose a new view.
        </Alert>
      )}
      {preference.defaultInvalid && (
        <Alert color="red" title="Saved default unavailable" role="alert">
          The saved default is invalid or its prefixes are no longer available.
          Save a new default to replace it.
        </Alert>
      )}
      {preference.error && (
        <Alert color="red" title="View not saved" role="alert">
          {preference.error}
        </Alert>
      )}
      {(summary.isError || query.isError) && (
        <Alert color="red" title="Pending List unavailable" role="alert">
          {pendingWorkError(summary.error || query.error)}
        </Alert>
      )}
      {preference.valid && summary.isSuccess && (
        <Paper withBorder>
          <ScrollArea type="auto">
            <Table
              horizontalSpacing="sm"
              verticalSpacing="sm"
              striped={false}
              highlightOnHover
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={48}>Details</Table.Th>
                  {columns.map((column) => (
                    <PendingWorkColumnHeader
                      key={column.id}
                      column={column}
                      sorts={preference.view.sorts}
                      onSort={handleSort}
                    />
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {query.data?.pages.map((page, index) => (
                  <PendingWorkTablePage
                    key={`${userId}:${index}`}
                    rows={page.data}
                    columns={preference.view.columns}
                    overdueThresholdDays={preference.view.overdueThresholdDays}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          <Stack p="md" align="center">
            <Text size="sm" aria-live="polite">
              {rows.length} loaded · {summary.data?.total} pending families
              across all prefixes
            </Text>
            {query.isLoading && (
              <Loader size="sm" aria-label="Loading pending cases" />
            )}
            {query.isSuccess && rows.length === 0 && (
              <Text>No pending work matches this view.</Text>
            )}
            <div ref={scrollRef} aria-hidden="true" />
            {query.hasNextPage && (
              <Button
                variant="light"
                onClick={handleMore}
                loading={query.isFetchingNextPage}
              >
                Load more cases
              </Button>
            )}
          </Stack>
        </Paper>
      )}
      <QueuePreferencesModal
        opened={settings}
        onClose={handleCloseSettings}
        currentPreferences={columnPreferences}
        onSave={handlePreferences}
        isSaving={false}
        columnDefinitions={PENDING_WORK_COLUMNS}
      />
    </Stack>
  );
}
